import React, { useState, useEffect } from 'react';
import { scannerAPI } from '../services/api';
import './LastScanResults.css';

function LastScanResults() {
    const [scanLogs, setScanLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadRecentScans();
        // Refresh every 30 seconds
        const interval = setInterval(loadRecentScans, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadRecentScans = async () => {
        try {
            const response = await scannerAPI.getRecentScanLogs();
            setScanLogs(response.data.data.scanLogs);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load scan logs');
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getStatusBadgeClass = (status) => {
        return status === 'SUCCESS' ? 'status-success' : 'status-failed';
    };

    const getValidationBadgeClass = (passed) => {
        return passed ? 'validation-passed' : 'validation-failed';
    };

    if (loading) {
        return (
            <div className="last-scan-results">
                <h3>Recent Scan Results</h3>
                <div className="loading-small">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="last-scan-results">
                <h3>Recent Scan Results</h3>
                <div className="error-small">{error}</div>
            </div>
        );
    }

    return (
        <div className="last-scan-results">
            <h3>Recent Scan Results (Last 5)</h3>
            {scanLogs.length === 0 ? (
                <p className="no-scans">No scan results yet</p>
            ) : (
                <div className="scan-table-wrapper">
                    <table className="scan-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Scanner</th>
                                <th>Document</th>
                                <th>Country</th>
                                <th>Scan Type</th>
                                <th>Status</th>
                                <th>Validation</th>
                                <th>Security</th>
                            </tr>
                        </thead>
                        <tbody>
                            {scanLogs.map((log) => (
                                <tr key={log.id}>
                                    <td className="timestamp">
                                        {formatTimestamp(log.scan_timestamp)}
                                    </td>
                                    <td className="scanner-name">
                                        {log.scannerName}
                                    </td>
                                    <td className="document-type">
                                        {log.document_type || '-'}
                                    </td>
                                    <td className="country">
                                        {log.issuing_country || '-'}
                                    </td>
                                    <td className="scan-type">
                                        <span className={`scan-type-badge ${log.is_double_sided ? 'double-sided' : 'single-sided'}`}>
                                            {log.is_double_sided ? 'Double-sided' : 'Single-sided'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusBadgeClass(log.scan_status)}`}>
                                            {log.scan_status}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`validation-badge ${getValidationBadgeClass(log.validation_passed)}`}>
                                            {log.validation_passed ? 'PASS' : 'FAIL'}
                                        </span>
                                    </td>
                                    <td className="security-checks">
                                        <span className="check-ratio">
                                            {log.security_checks_passed}/{log.security_checks_total}
                                        </span>
                                        {log.security_checks_failed > 0 && (
                                            <span className="failed-count">
                                                ({log.security_checks_failed} failed)
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default LastScanResults;
